import type { ModelInfo, User } from './index.ts';
import type { ModelPricing } from './usage.ts';

/** Identifier syntax only. The server registry, never this predicate, grants access. */
export function isModelId(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9._:/-]{0,199}$/.test(value);
}
export interface PlatformModelCapabilities {
  family: 'gpt' | 'other';
  pdfInput: 'native-file' | 'text-images' | 'text-only' | 'unsupported';
  acceptsImages: boolean;
  nativePdfPageRendering: boolean;
  /** Raw native context window, before Core's own usable-window allowance. */
  contextWindow: number;
  autoCompactTokenLimit: number | null;
  /** Preserve means omit the native override and retain the existing provider behavior. */
  compaction: 'preserve' | 'local' | 'v2';
  reasoningEfforts: string[];
  defaultReasoningEffort: string | null;
}
export interface PlatformModelRates {
  inputPerMillion: number;
  outputPerMillion: number;
  cachedInputPerMillion: number;
  cacheWriteInputPerMillion: number;
  cacheWritePolicy: 'included_in_input' | 'separate';
}
export interface ModelConnectionTest {
  testedAt: string;
  revision: number;
  success: boolean;
  message: string;
  checks: Array<{ name: string; ok: boolean; message: string }>;
  latencyMs?: number;
}
/** Administrative DTO. Credentials are write-only and never enter this object. */
export interface PlatformModelRecord {
  id: string;
  displayName: string;
  supplier: string;
  upstreamModel: string;
  baseUrl: string;
  enabled: boolean;
  isDefault: boolean;
  hasApiKey: boolean;
  credentialSource: 'environment' | 'stored';
  revision: number;
  capabilities: PlatformModelCapabilities;
  pricing: ModelPricing;
  test: ModelConnectionTest | null;
  createdAt: string;
  updatedAt: string;
}
export interface PlatformModelInput {
  id: string;
  displayName: string;
  supplier: string;
  upstreamModel: string;
  baseUrl: string;
  /** Omit when keeping the saved credential. Empty is not a request to reveal it. */
  apiKey?: string;
  enabled: boolean;
  capabilities: PlatformModelCapabilities;
  pricing: PlatformModelRates;
}
export type PlatformModelUpdate = Omit<PlatformModelInput, 'id'> & { revision: number };
export interface PlatformModelsPage { models: PlatformModelRecord[]; defaultModelId: string; defaultRevision: number }
export interface PlatformModelResponse { model: PlatformModelRecord; defaultRevision?: number }
export interface PlatformModelTestResponse extends PlatformModelResponse { result: ModelConnectionTest }
export interface PlatformModelCatalog { models: ModelInfo[]; defaultModelId: string; version?: string }
export interface AdminRoleUsersPage { users: User[]; total: number; page: number; pageSize: number }
export interface AdminRoleChange { role: 'user' | 'admin' }
export function canManagePlatform(role: User['role']): boolean { return role === 'admin' || role === 'super_admin'; }
export function canManageRoles(role: User['role']): boolean { return role === 'super_admin'; }
